import { FC, FormEvent, useEffect, useState } from 'react'

import { useDispatch } from 'react-redux'

import { LicenseBooklet } from '@components/UI/LicenseBooklet/LicenseBooklet'
import Loading from '@components/UI/Loading'
import { useModal } from '@hooks/useModal'
import { ApprovalType } from '@store/campaigns'
import { fetchMediaObjectLicenseOptions, ILicense } from '@store/licenses'
import { purchaseLicense } from '@store/mediaObjects'
import { clearUIFlag, UIFlag } from '@store/ui'
import { CloseIcon, Modal } from '@tribegroup'
import { isEmpty } from '@utils/isEmpty'
import { PurchaseLicenseFooter } from './Footer'
import { PurchaseLicenseHeader } from './Header'
import { PurchaseLicensePriceSummary } from './PriceSummary'
import { usePurchaseLicenseData } from './usePurchaseLicenseData'

interface IProps {
  approvalType?: ApprovalType
  submissionId?: number
  frameNumber?: number
  currentMediaObjectId?: number
}

export const PurchaseLicense: FC<IProps> = ({
  approvalType,
  frameNumber,
  submissionId,
  currentMediaObjectId,
}) => {
  const { isOpen, hideModal } = useModal()
  const dispatch = useDispatch()

  const [selectedLicenseIndex, setSelectedLicenseIndex] = useState(0)

  const {
    mediaObject,
    licenseOptions,
    isFetching,
    isInboxView,
    showPriceSummary,
    shouldHideModal,
    submission,
    isUnlockedCampaign,
    campaignId,
  } = usePurchaseLicenseData({
    approvalType,
    frameNumber,
    submissionId,
    currentMediaObjectId,
  })

  useEffect(() => {
    dispatch(fetchMediaObjectLicenseOptions(mediaObject.id))
  }, [])

  // Auto-select "Perpetual All Channels" for unlocked campaigns
  useEffect(() => {
    if (isUnlockedCampaign && licenseOptions.length > 0) {
      // Find "Perpetual All Channels" license type
      const perpetualAllChannelsIndex = licenseOptions.findIndex(
        (option) => option.license_type === 'perpetual_exclusive_all_rights',
      )
      if (perpetualAllChannelsIndex !== -1) {
        setSelectedLicenseIndex(perpetualAllChannelsIndex)
      }
    }
  }, [isUnlockedCampaign, licenseOptions])

  useEffect(() => {
    if (shouldHideModal) {
      hideModal()
      dispatch(clearUIFlag(UIFlag.HIDE_MODAL))
    }
  }, [shouldHideModal])

  if (isFetching && isEmpty(licenseOptions)) {
    return (
      <Modal reactModalProps={{ isOpen: Boolean(isOpen) }} width={55} padding={0}>
        <Loading />
      </Modal>
    )
  }

  const disableSubmit =
    approvalType === 'final-approval' &&
    submission?.final_media_type &&
    submission?.final_media_type !== mediaObject.media_type

  const selectLicenseOption = (event: FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement
    const elementValue = target.value

    const index = licenseOptions.findIndex((value) => `${value.license_type}` === `${elementValue}`)

    setSelectedLicenseIndex(index)
  }

  const onClick = () => {
    if (disableSubmit) {
      return
    }

    dispatch(
      purchaseLicense(mediaObject?.id, selectedLicenseIndex, campaignId, submissionId, {
        refreshSubmissions: isInboxView,
        purchaseType: approvalType,
      }),
    )
  }

  return (
    <Modal
      reactModalProps={{ isOpen: Boolean(isOpen) }}
      width={isUnlockedCampaign ? 27.5 : 55}
      padding={0}
      mobilePadding={0}
    >
      <CloseIcon onClick={hideModal} />
      <LicenseBooklet
        mediaObject={mediaObject}
        licenseOptions={licenseOptions}
        header={
          <PurchaseLicenseHeader
            mediaObject={mediaObject}
            approvalType={approvalType}
            isUnlockedCampaign={isUnlockedCampaign}
          />
        }
        onSelectLicenseOption={selectLicenseOption}
        finalMediaType={submission?.final_media_type}
        isUnlockedCampaign={isUnlockedCampaign}
      >
        {showPriceSummary && (
          <PurchaseLicensePriceSummary
            mediaObject={mediaObject}
            approvalType={approvalType}
            license={
              (mediaObject.transacted_license &&
              mediaObject.transacted_license?.status !== 'request_expired'
                ? mediaObject.transacted_license
                : licenseOptions[selectedLicenseIndex]) as ILicense
            }
          />
        )}

        <PurchaseLicenseFooter
          onClick={onClick}
          mediaObject={mediaObject}
          approvalType={approvalType}
          disableSubmit={disableSubmit}
        />
      </LicenseBooklet>
    </Modal>
  )
}
